//UTILS
import { PATH_APP } from "../../../routes/paths";
import { store } from "../../../store/store";
import { openOperationMEPModal } from "../../../store/slices/operationMEPSlice";

//TYPES
import { NavItem } from "../interfaces/types";
import { openCryptoModal } from "../../../store/slices/cryptoSlice";
import { UserData } from "../../../types/types";

// CAPACITOR
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import useFeatures from "../../../hooks/useFeatures";

const operateWhitelist = [
  "leonel.rios@almafintech.com.ar",
  "agustin.pecci@almafintech.com.ar",
  "carlos.araujo@almafintech.com.ar",
  "marina.derito@allaria.com.ar",
  "mariano.mazzaccaro@allaria.com.ar",
  "agustin.rossi@almafintech.com.ar",
  "jmalvarez1985@hotmail.com",
  "mariana.gallo@allaria.com.ar",
  "cristian.gonzalez@allaria.com.ar",
  "alejandro.schwartzmann@allaria.com.ar",
  "danielavazquez95@gmail.com",
  "alex@almafintech.com.ar",
  "mderito@hotmail.com",
  "alejandro.schwartzmann@allaria.com.ar",
  "ruizjuanignacio91@gmail.com",
  "arossi@allaria.com.ar",
  "guido.gandon@almafintech.com.ar",
  "leonelriosrc@gmail.com",
  "agustinpecci@gmail.com",
];

export const isInWhitelist = (): boolean => {
  const state = store.getState();
  const userEmail = state.authentication.user?.email;

  if (!userEmail) return false;
  return operateWhitelist.includes(userEmail);
};

const handleMEPClick = (): void => {
  store.dispatch(openOperationMEPModal());
};

const handleCryptoClick = (): void => {
  store.dispatch(openCryptoModal());
};

const isCryptoUser = (user: UserData | null) => {
  return user?.authorization?.crypto?.roles?.includes("VIEW_ALL");
};

const isCryptoOnly = (user: UserData | null) => {
  return user?.authorization?.crypto?.roles?.includes("CRYPTO_ONLY");
};

export const getNavItems = (
  user: UserData | null,
  features?: ReturnType<typeof useFeatures>
): NavItem[] => [
  {
    key: "home",
    label: "Inicio",
    pathname: PATH_APP.home,
  },
  {
    key: "portfolio",
    label: "Cartera",
    pathname: PATH_APP.portfolio,
    collapsableGroup: {
      items: [
        {
          key: "valuation",
          label: "Valuación",
          pathname: PATH_APP.valuation,
        },
        {
          key: "returns",
          label: "Evolución",
          pathname: PATH_APP.returns,
        },
        {
          key: "reports",
          label: "Informes",
          pathname: PATH_APP.reports,
        },
      ],
    },
  },
  {
    key: "operate",
    label: "Operar",
    pathname: PATH_APP.market,
  },
  {
    key: "dolar-mep",
    label: "Dólar MEP",
    onClick: handleMEPClick,
    translate: false,
    isDisabled: !!features && !features.isMepEnabled,
  },
  {
    key: "activity",
    label: "Actividad",
    pathname: PATH_APP.activity,
  },
  {
    key: "transactions",
    label: "Transferencias",
    pathname: PATH_APP.transactions,
  },
  {
    key: "research",
    label: "Research",
    pathname: PATH_APP.research,
  },
  {
    key: "help",
    label: "Ayuda",
    pathname: PATH_APP.help,
  },
  ...(isCryptoUser(user) || isCryptoOnly(user)
    ? [
        {
          key: "allaria-digital",
          label: "Allaria Digital",
          onClick: handleCryptoClick,
        },
      ]
    : []),
];

/**
 * Determines if a URL is an absolute external HTTP(S) URL.
 */
export const isExternalHttpUrl = (url: string) => /^https?:\/\//i.test(url);

export type ExternalOpenMethod = "browser" | "window";
export type ExternalOpenResult = { method: ExternalOpenMethod };

/**
 * Opens an external URL using Capacitor Browser when running natively,
 * otherwise falls back to window.open on the web. Safe for mailto/tel too.
 * Returns the navigation method used to help callers coordinate guards.
 */
export const openExternal = async (
  url: string,
  fallbackTarget?: string,
): Promise<ExternalOpenResult> => {
  const isNative = Capacitor.isNativePlatform();
  const isHttp = isExternalHttpUrl(url);
  const target = fallbackTarget ?? "_self";
  const usesIsolatedContext = target === "_blank";

  if (isNative && isHttp) {
    const hasBrowser = Capacitor.isPluginAvailable("Browser");
    if (!hasBrowser) {
      // Helpful debug message when plugin isn’t installed in native project
      console.warn(
        "Capacitor Browser plugin is not available on native. Falling back to window.open. Did you run `npx cap sync` and rebuild the native app?",
      );
    } else {
      try {
        await Browser.open({ url });
        return { method: "browser" };
      } catch (e) {
        console.error("Browser.open failed, falling back to window.open", e);
      }
    }
  }

  // Fallback for web or non-http schemes (mailto:, tel:, etc.)
  const win = window.open(
    url,
    target,
    usesIsolatedContext ? "noopener,noreferrer" : undefined,
  );

  // When using noopener the browser intentionally returns null even on success; only fall back when we truly expect a handle.
  if (!win && !usesIsolatedContext) {
    window.location.href = url;
  } else if (!win && usesIsolatedContext) {
    console.warn(
      "window.open returned null with noopener; assuming navigation succeeded to avoid duplicate redirects",
    );
  }

  return { method: "window" };
};
